import React, { Fragment, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import { useReactToPrint } from 'react-to-print';
import { DialogHeader, DialogHeading } from '../Episode/Episode.Styled';

const ProviderAdmRatesDialog = ({ open, onClose, reportData, loading }) => {
  const componentRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: `
      @page {
        size: landscape;
        padding-right: 20mm;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
          font-size: 9px;
        }
        table {
          font-size: 8px;
          width: 100%;
          table-layout: fixed;
        }
        th, td {
          padding: 1px 2px !important;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
        h4 {
          font-size: 14px;
        }
        h5 {
          font-size: 12px;
        }
        h6 {
          font-size: 10px;
        }
      }
    `,
  });

  // Group data by County > Modality > Gender
  const groupData = (data) => {
    const grouped = {};

    data.forEach(item => {
      // Level 1: County
      if (!grouped[item.CnyName]) {
        grouped[item.CnyName] = {};
      }

      // Level 2: Modality
      if (!grouped[item.CnyName][item.ModDsc]) {
        grouped[item.CnyName][item.ModDsc] = {};
      }

      // Level 3: Gender
      if (!grouped[item.CnyName][item.ModDsc][item.GndName]) {
        grouped[item.CnyName][item.ModDsc][item.GndName] = [];
      }

      grouped[item.CnyName][item.ModDsc][item.GndName].push(item);
    });

    return grouped;
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (!reportData || !reportData.data || reportData.data.length === 0) {
      return (
        <Box sx={{ p: 4 }}>
          <Typography>No data available for this report.</Typography>
        </Box>
      );
    }

    const groupedData = groupData(reportData.data);

    return (
      <Box ref={componentRef} sx={{ p: 2 }}>
        <Typography variant="h5" sx={{ mb: 2, textAlign: 'center' }}>
          Provider Admin Contact and Rates Report
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, textAlign: 'center', color: 'text.secondary' }}>
          {reportData.message}
        </Typography>

        {/* Iterate through Counties */}
        {Object.keys(groupedData).map((county) => (
          <Fragment key={county}>
            <Box sx={{ mt: 3, mb: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1890ff' }}>
                County: {county}
              </Typography>
            </Box>

            {/* Iterate through Modalities within County */}
            {Object.keys(groupedData[county]).map((modality) => (
              <Fragment key={`${county}-${modality}`}>
                <Box sx={{ mt: 2, mb: 1, ml: 0 }}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    Modality: {modality}
                  </Typography>
                </Box>

                {/* Iterate through Genders within Modality */}
                {Object.keys(groupedData[county][modality]).map((gender) => (
                  <Fragment key={`${county}-${modality}-${gender}`}>
                    <Box sx={{ ml: 0, mb: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Gender: {gender}
                      </Typography>
                    </Box>

                    <TableContainer component={Paper} sx={{ ml: 0, mb: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#747070' }}>
                            <TableCell sx={{ color: '#fff' }}>CBP ID</TableCell>
                            <TableCell sx={{ color: '#fff' }}>Organization</TableCell>
                            <TableCell sx={{ color: '#fff' }}>Org Address</TableCell>
                            <TableCell sx={{ color: '#fff' }}>Admin Contact</TableCell>
                            <TableCell sx={{ color: '#fff' }}>Admin Phone</TableCell>
                            <TableCell sx={{ color: '#fff' }}>Admin Fax</TableCell>
                            <TableCell sx={{ color: '#fff' }}>CBP Name</TableCell>
                            <TableCell sx={{ color: '#fff' }}>CBP Address</TableCell>
                            <TableCell sx={{ color: '#fff' }}>SAP</TableCell>
                            <TableCell sx={{ color: '#fff' }}>290</TableCell>
                            <TableCell sx={{ color: '#fff' }}>CCC</TableCell>
                            <TableCell sx={{ color: '#fff' }}>Rate Type</TableCell>
                            <TableCell sx={{ color: '#fff' }}>Start</TableCell>
                            <TableCell sx={{ color: '#fff' }}>End</TableCell>
                            <TableCell sx={{ color: '#fff' }}>Amount</TableCell>
                            <TableCell width="80" sx={{ color: '#fff' }}>FY</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {groupedData[county][modality][gender].map((provider) => {
                            // Each provider may have multiple rates
                            const rateCount = provider.rates.length;

                            return provider.rates.map((rate, rateIdx) => (
                              <TableRow key={`${provider.CboID}-${rateIdx}`}>
                                {rateIdx === 0 ? (
                                  <>
                                    <TableCell rowSpan={rateCount}>{provider.CboID}</TableCell>
                                    <TableCell rowSpan={rateCount}>{provider.OrgName}</TableCell>
                                    <TableCell rowSpan={rateCount}>{provider.OrgAddress}</TableCell>
                                    <TableCell rowSpan={rateCount}>{provider.AdmCnt}</TableCell>
                                    <TableCell rowSpan={rateCount}>{provider.AdmPh}</TableCell>
                                    <TableCell rowSpan={rateCount}>{provider.AdmFax}</TableCell>
                                    <TableCell rowSpan={rateCount}>{provider.CboName}</TableCell>
                                    <TableCell rowSpan={rateCount}>{provider.CboAddress}</TableCell>
                                    <TableCell rowSpan={rateCount}>
                                      {provider.CbpSapYN ? 'Yes' : 'No'}
                                    </TableCell>
                                    <TableCell rowSpan={rateCount}>
                                      {provider.CbpAcpt290 ? 'Yes' : 'No'}
                                    </TableCell>
                                    <TableCell rowSpan={rateCount}>
                                      {provider.CbpAcptccc ? 'Yes' : 'No'}
                                    </TableCell>
                                  </>
                                ) : null}
                                <TableCell>{rate.CboRte_TmtTypID}</TableCell>
                                <TableCell>{rate.CboRteStart_MthID}</TableCell>
                                <TableCell>{rate.CboRteEnd_MthID}</TableCell>
                                <TableCell>
                                  {rate.CboRteAmt.toLocaleString('en-US', {
                                    style: 'currency',
                                    currency: 'USD'
                                  })}
                                </TableCell>
                                <TableCell>{rate.Mth_FyID}</TableCell>
                              </TableRow>
                            ));
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Fragment>
                ))}
              </Fragment>
            ))}
          </Fragment>
        ))}
      </Box>
    );
  };

  return (
    <Dialog fullScreen open={open} onClose={onClose}>
      <DialogHeader>
        <DialogHeading variant="h5">
          Provider Admin Contact and Rates Report
        </DialogHeading>
        <Box>
          <IconButton onClick={handlePrint} disabled={loading || !reportData}>
            <PrintIcon />
          </IconButton>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogHeader>
      <DialogContent>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
};

export default ProviderAdmRatesDialog;
